/**
 * Inbox feedback (May 2026) — cross-component refresh signal for the header's
 * unread *message* badge.
 *
 * Background: the header's unread-message count is normally refreshed by a
 * Supabase realtime `postgres_changes` subscription on the `messages` table
 * (see `Header.tsx`). When that channel isn't connected — no Supabase client
 * configured, the websocket dropped, the table isn't in the realtime
 * publication, an event was missed, RLS prevents the row from being
 * broadcast, etc. — the badge stays at "1" even after the user has opened
 * the thread and the server has flipped `messages.is_read = true`. Users
 * see a red "1" on the message icon while sitting on the conversation
 * page that already shows the message as read.
 *
 * Mirrors the `notifications-events` bus so REST-driven reads can ping the
 * header to recount independent of realtime. Emit this whenever client code
 * causes the server to mutate `messages.is_read` (today: opening a
 * conversation via `fetchConversation`).
 */

type Listener = () => void;

const listeners = new Set<Listener>();

export function onMessagesChanged(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitMessagesChanged(): void {
  for (const listener of listeners) {
    try {
      listener();
    } catch (err) {
      console.warn("[messages-events] listener threw:", err);
    }
  }
}

/**
 * Task #4743 — cross-*tab* unread sync.
 *
 * The pub/sub above only reaches listeners in the SAME tab. When a user has
 * the inbox open in two tabs and reads a conversation in one of them, the
 * other tab keeps showing that thread as unread (dot / bold / "Unread (N)")
 * until its own window-focus or periodic refetch fires.
 *
 * We broadcast the just-read conversation's `public_id` over a
 * `BroadcastChannel` so any other open tab can clear its indicator promptly
 * and then reconcile against the server. The broadcast is sent only *after*
 * the reading tab's `fetchConversation` has resolved (i.e. the server has
 * already flipped `messages.is_read = true`), so a receiving tab that
 * refetches sees the read state and never bounces the indicator back.
 *
 * `BroadcastChannel` is widely supported, but when it isn't available the
 * broadcast/subscribe helpers degrade to no-ops and the existing
 * refetch-on-focus path remains the source of truth — nothing depends on
 * the channel being present.
 */
const CONVERSATION_READ_CHANNEL = "bac:conversation-read";

type ConversationReadListener = (publicId: string) => void;

const readListeners = new Set<ConversationReadListener>();

let readChannel: BroadcastChannel | null = null;
let readChannelInit = false;

function getReadChannel(): BroadcastChannel | null {
  if (readChannelInit) return readChannel;
  readChannelInit = true;
  if (typeof BroadcastChannel === "undefined") {
    readChannel = null;
    return null;
  }
  try {
    const channel = new BroadcastChannel(CONVERSATION_READ_CHANNEL);
    // BroadcastChannel does not echo a message back to the tab that posted
    // it, so this only fires for reads that happened in *other* tabs.
    channel.onmessage = (ev: MessageEvent) => {
      const pid = (ev?.data as { publicId?: unknown } | null)?.publicId;
      if (typeof pid !== "string" || !pid) return;
      for (const listener of readListeners) {
        try {
          listener(pid);
        } catch (err) {
          console.warn("[messages-events] cross-tab listener threw:", err);
        }
      }
    };
    readChannel = channel;
  } catch (err) {
    console.warn("[messages-events] BroadcastChannel unavailable:", err);
    readChannel = null;
  }
  return readChannel;
}

/**
 * Tell other open tabs that `publicId` has just been read by this tab.
 * Call this only after the server has acknowledged the read so a receiving
 * tab's reconciling refetch can't race ahead of the server truth.
 */
export function broadcastConversationRead(publicId: string): void {
  if (!publicId) return;
  const channel = getReadChannel();
  if (!channel) return;
  try {
    channel.postMessage({ publicId });
  } catch (err) {
    console.warn("[messages-events] cross-tab broadcast failed:", err);
  }
}

/**
 * Subscribe to "a conversation was read in another tab" signals. The
 * listener receives the read conversation's `public_id`. Returns an
 * unsubscribe function. No-op (still safe to call/clean up) when
 * `BroadcastChannel` isn't supported.
 */
export function onConversationReadAcrossTabs(
  listener: ConversationReadListener,
): () => void {
  // Ensure the channel exists so its `onmessage` is wired up to receive.
  getReadChannel();
  readListeners.add(listener);
  return () => {
    readListeners.delete(listener);
  };
}
